import { useContext, useEffect, useRef, useState } from "react";
import { usePlaidLink } from "react-plaid-link";
import { WriteBatch } from "firebase/firestore";
import { lineWobble } from "ldrs";
import {
  AccountStatuses,
  BreadAccount,
  BreadSearchInstitution,
} from "breadcommon";
import { RyeModalContent, useRyeModal } from "../rye/RyeModal";
import { RyeIcon } from "../rye/RyeIcon";
import { UserContext } from "../firebaseio/UserContext";
import { AccountsContext } from "../firebaseio/AccountsContext";
import { callFirebaseFunction } from "../firebaseio/firebaseFunctions";
import {
  firestoreDeleteAccountById,
  firestoreUpdateAccount,
} from "../firebaseio/firestoreIo";
import { CandidateAccountRow } from "./CandidateAccountRow";
import { InstitutionSearch } from "./InstitutionSearch";
import { buildClasses } from "../utils/buildClasses";
import {
  getLoadedInitialInstitutions,
  loadInitialInstitutions,
  warmUpInstitutionSearch,
} from "./institutionSearchPrefetch";
import { SyncStartModalContent } from "./SyncStartModalContent";

lineWobble.register();

enum AddNewAccountFlowState {
  SELECT_BANK,
  PRE_LINK_NOTE,
  PLAID_LINK_OPENING,
  LINKING_ACCOUNTS,
  CHOOSING_SYNC_START,
  CONFIRMING_ACCOUNTS,
  SYNCING,
  UPDATE_PRE_LINK_NOTE,
  UPDATE_PLAID_LINK_OPENING,
  UPDATE_INITIALIZING_ACCOUNTS,
  UPDATE_CHOOSING_SYNC_START,
  UPDATE_CONFIRMING_ACCOUNTS,
  UPDATE_SYNCING,
}

export function AddNewAccountModal(): JSX.Element {
  const { closeModal } = useRyeModal();
  const user = useContext(UserContext);
  const accounts = useContext(AccountsContext);

  const [flowState, setFlowState] = useState<AddNewAccountFlowState>(
    AddNewAccountFlowState.SELECT_BANK,
  );

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchResults, setSearchResults] = useState<BreadSearchInstitution[]>(
    [],
  );
  const [initialInstitutions, setInitialInstitutions] = useState<
    BreadSearchInstitution[]
  >(() => getLoadedInitialInstitutions() ?? []);
  const [selectedInstitution, setSelectedInstitution] =
    useState<BreadSearchInstitution | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [publicToken, setPublicToken] = useState<string | null>(null);

  const [shouldFocus, setShouldFocus] = useState<boolean>(true);

  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const accountUpdatePromises = useRef<Promise<void | WriteBatch>[]>([]);

  // Update flow state
  const [updatePlaidItemId, setUpdatePlaidItemId] = useState<string | null>(
    null,
  );
  const [expectedNewAccounts, setExpectedNewAccounts] = useState<number | null>(
    null,
  );

  // Show the popular banks grid, fetching it if it wasn't prefetched, and
  // start the search function so the first search is quick.
  useEffect(() => {
    warmUpInstitutionSearch();
    if (getLoadedInitialInstitutions() !== null) return;
    async function loadInitial() {
      setIsLoading(true);
      try {
        setInitialInstitutions(await loadInitialInstitutions());
      } catch (err) {
        console.error("Failed to load popular institutions:", err);
      } finally {
        setIsLoading(false);
      }
    }
    loadInitial();
  }, []);

  // Debounced live search
  const handleSearchChange = (text: string) => {
    setSearchQuery(text);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    if (!text.trim()) {
      setSearchResults([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const result = await callFirebaseFunction("SearchInstitutions", {
          query: text,
        });
        if (result.data.success && result.data.institutions) {
          setSearchResults(result.data.institutions);
        }
      } catch (err) {
        console.error("Search institutions failed:", err);
      } finally {
        setIsLoading(false);
      }
    }, 300);
  };

  // Launch Plaid Link
  const { open: openPlaid, ready: plaidReady } = usePlaidLink({
    token: linkToken,
    onSuccess: (pt, metadata) => {
      if (metadata.institution) {
        setSelectedInstitution({
          institution_id: metadata.institution.institution_id,
          name: metadata.institution.name,
          url: null,
          logo: null,
          primary_color: null,
        });
      }
      if (updatePlaidItemId) {
        // Update flow: no public token exchange needed
        setFlowState(AddNewAccountFlowState.UPDATE_INITIALIZING_ACCOUNTS);
      } else {
        // New flow: exchange public token
        setPublicToken(pt);
        setFlowState(AddNewAccountFlowState.LINKING_ACCOUNTS);
      }
    },
    onExit: (err) => {
      if (err) {
        console.error("Plaid link exited with error:", err);
      }
      setSelectedInstitution(null);
      setLinkToken(null);
      setUpdatePlaidItemId(null);
      setFlowState(AddNewAccountFlowState.SELECT_BANK);
    },
  });

  // Call Plaid Link open when token is ready
  useEffect(() => {
    if (
      (flowState === AddNewAccountFlowState.PLAID_LINK_OPENING ||
        flowState === AddNewAccountFlowState.UPDATE_PLAID_LINK_OPENING) &&
      linkToken &&
      plaidReady
    ) {
      openPlaid();
    }
  }, [flowState, linkToken, plaidReady, openPlaid]);

  // Handle bank selection
  const handleSelectBank = (bank: BreadSearchInstitution) => {
    // Check if we already have accounts linked for this institution
    const existingAccount = Array.from(accounts.values()).find(
      (acc) => acc.institution_id === bank.institution_id,
    );

    if (existingAccount && existingAccount.plaid_item_id) {
      // Existing Plaid item — use update flow inline
      setUpdatePlaidItemId(existingAccount.plaid_item_id);
      setSelectedInstitution(bank);
      setFlowState(AddNewAccountFlowState.UPDATE_PRE_LINK_NOTE);
    } else {
      // Go to intermediate Pre-Link Note screen
      setSelectedInstitution(bank);
      setFlowState(AddNewAccountFlowState.PRE_LINK_NOTE);
    }
  };

  // Trigger link token fetch and open Plaid
  const handleProceedToLink = async () => {
    if (!selectedInstitution) return;
    setFlowState(AddNewAccountFlowState.PLAID_LINK_OPENING);
    try {
      console.log("Institution ID: ", selectedInstitution.institution_id);
      const result = await callFirebaseFunction("GetPlaidLinkToken", {
        institution_id: selectedInstitution.institution_id,
      });
      if (result.data.success && result.data.linkToken) {
        setLinkToken(result.data.linkToken.link_token);
      } else {
        throw new Error(result.data.error_msg || "Failed to create link token");
      }
    } catch (err) {
      console.error("Error creating link token:", err);
      setSelectedInstitution(null);
      setFlowState(AddNewAccountFlowState.SELECT_BANK);
    }
  };

  // Trigger update link token fetch and open Plaid
  const handleProceedToUpdateLink = async () => {
    if (!updatePlaidItemId) return;
    setFlowState(AddNewAccountFlowState.UPDATE_PLAID_LINK_OPENING);
    try {
      const result = await callFirebaseFunction("GetPlaidLinkUpdateToken", {
        plaid_item_id: updatePlaidItemId,
        account_selection_enabled: true,
      });
      if (result.data.success && result.data.linkToken) {
        setLinkToken(result.data.linkToken.link_token);
      } else {
        throw new Error(
          result.data.error_msg || "Failed to create update link token",
        );
      }
    } catch (err) {
      console.error("Error creating update link token:", err);
      setUpdatePlaidItemId(null);
      setSelectedInstitution(null);
      setFlowState(AddNewAccountFlowState.SELECT_BANK);
    }
  };

  // Exchanging public token
  useEffect(() => {
    if (flowState === AddNewAccountFlowState.LINKING_ACCOUNTS && publicToken) {
      const exchange = async () => {
        try {
          const result = await callFirebaseFunction("ExchangePublicToken", {
            public_token: publicToken,
          });
          if (result.data.success) {
            setFlowState(AddNewAccountFlowState.CHOOSING_SYNC_START);
          } else {
            throw new Error(result.data.error_msg || "Exchange failed");
          }
        } catch (err) {
          console.error("Exchanging public token failed:", err);
          setSelectedInstitution(null);
          setLinkToken(null);
          setPublicToken(null);
          setFlowState(AddNewAccountFlowState.SELECT_BANK);
        }
      };
      exchange();
    }
  }, [flowState, publicToken]);

  // Initialize update accounts after Plaid Link update success
  useEffect(() => {
    if (
      flowState === AddNewAccountFlowState.UPDATE_INITIALIZING_ACCOUNTS &&
      updatePlaidItemId
    ) {
      const init = async () => {
        try {
          const result = await callFirebaseFunction(
            "InitializePlaidUpdateAccounts",
            { plaid_item_id: updatePlaidItemId },
          );
          if (result.data.success) {
            const numNew = result.data.numNewAccounts ?? 0;
            setExpectedNewAccounts(numNew);
            if (numNew > 0) {
              setFlowState(AddNewAccountFlowState.UPDATE_CHOOSING_SYNC_START);
            } else {
              setFlowState(AddNewAccountFlowState.UPDATE_SYNCING);
            }
          } else {
            console.error(
              "Failed to initialize update accounts:",
              result.data.error_msg,
            );
            setFlowState(AddNewAccountFlowState.SELECT_BANK);
          }
        } catch (err) {
          console.error("Error initializing update accounts:", err);
          setFlowState(AddNewAccountFlowState.SELECT_BANK);
        }
      };
      init();
    }
  }, [flowState, updatePlaidItemId]);

  // Sync transactions and accounts after update
  useEffect(() => {
    if (flowState === AddNewAccountFlowState.UPDATE_SYNCING) {
      const runSyncs = async () => {
        try {
          await callFirebaseFunction("SyncTransactions", {});
          await callFirebaseFunction("SyncAccounts", {});
        } catch (err) {
          console.error("Error syncing after update:", err);
        } finally {
          closeModal();
        }
      };
      runSyncs();
    }
  }, [closeModal, flowState]);

  // Finish confirmation and run transaction sync
  const handleConfirmCandidates = async () => {
    setFlowState(AddNewAccountFlowState.SYNCING);
    try {
      await Promise.allSettled(accountUpdatePromises.current);

      let nextPositionNum = Array.from(accounts.values()).filter(
        (acc) =>
          acc.status !== AccountStatuses.NEW_SELECTED &&
          acc.status !== AccountStatuses.NEW_UNSELECTED,
      ).length;

      for (const [accountId, account] of Array.from(accounts)) {
        if (
          account.institution_id === selectedInstitution?.institution_id &&
          (account.status === AccountStatuses.NEW_SELECTED ||
            account.status === AccountStatuses.NEW_UNSELECTED)
        ) {
          if (account.status === AccountStatuses.NEW_SELECTED) {
            await firestoreUpdateAccount(user.uid, accountId, {
              system_data: { status: AccountStatuses.ACTIVE },
              manual_data: { order_position: nextPositionNum },
            });
            nextPositionNum++;
          } else {
            await firestoreDeleteAccountById(user.uid, account, null);
          }
        }
      }

      await callFirebaseFunction("SyncTransactions", {});
      await callFirebaseFunction("SyncAccounts", {});
      closeModal();
    } catch (err) {
      console.error("Failed to sync and confirm candidate accounts:", err);
      closeModal();
    }
  };

  const handleCancelCandidates = async () => {
    setFlowState(AddNewAccountFlowState.SYNCING);
    try {
      for (const [, account] of Array.from(accounts)) {
        if (
          account.institution_id === selectedInstitution?.institution_id &&
          (account.status === AccountStatuses.NEW_SELECTED ||
            account.status === AccountStatuses.NEW_UNSELECTED)
        ) {
          await firestoreDeleteAccountById(user.uid, account, null);
        }
      }
      await callFirebaseFunction("RemoveUnusedItemsAndInstitutions", {});
    } catch (err) {
      console.error("Error cleaning up candidate accounts:", err);
    } finally {
      closeModal();
    }
  };

  // Confirm update candidates
  const handleConfirmUpdateCandidates = async () => {
    try {
      await Promise.allSettled(accountUpdatePromises.current);

      let nextPositionNum = Array.from(accounts.values()).filter(
        (acc) =>
          acc.status !== AccountStatuses.NEW_SELECTED &&
          acc.status !== AccountStatuses.NEW_UNSELECTED,
      ).length;

      for (const [accountId, account] of Array.from(accounts)) {
        if (account.plaid_item_id === updatePlaidItemId) {
          if (account.status === AccountStatuses.NEW_SELECTED) {
            await firestoreUpdateAccount(user.uid, accountId, {
              system_data: { status: AccountStatuses.ACTIVE },
              manual_data: { order_position: nextPositionNum },
            });
            nextPositionNum++;
          } else if (account.status === AccountStatuses.NEW_UNSELECTED) {
            await firestoreDeleteAccountById(user.uid, account, null);
          }
        }
      }

      setFlowState(AddNewAccountFlowState.UPDATE_SYNCING);
    } catch (err) {
      console.error("Failed to confirm update candidates:", err);
      setFlowState(AddNewAccountFlowState.UPDATE_SYNCING);
    }
  };

  // Cancel update candidates
  const handleCancelUpdateCandidates = async () => {
    try {
      for (const [, account] of Array.from(accounts)) {
        if (
          account.plaid_item_id === updatePlaidItemId &&
          (account.status === AccountStatuses.NEW_SELECTED ||
            account.status === AccountStatuses.NEW_UNSELECTED)
        ) {
          await firestoreDeleteAccountById(user.uid, account, null);
        }
      }
      await callFirebaseFunction("RemoveUnusedItemsAndInstitutions", {});
    } catch (err) {
      console.error("Error cleaning up update candidates:", err);
    } finally {
      closeModal();
    }
  };

  const newCandidateAccounts = Array.from(accounts.values()).filter(
    (acc) =>
      acc.institution_id === selectedInstitution?.institution_id &&
      (acc.status === AccountStatuses.NEW_SELECTED ||
        acc.status === AccountStatuses.NEW_UNSELECTED),
  );
  const updateCandidateAccounts = Array.from(accounts.values()).filter(
    (acc) =>
      acc.plaid_item_id === updatePlaidItemId &&
      (acc.status === AccountStatuses.NEW_SELECTED ||
        acc.status === AccountStatuses.NEW_UNSELECTED),
  );

  // Build the complete modal view for the current flow state
  const getModalContent = (): React.ReactNode => {
    switch (flowState) {
      case AddNewAccountFlowState.CHOOSING_SYNC_START:
        return (
          <SyncStartModalContent
            candidateAccounts={newCandidateAccounts}
            expectedCount={null}
            onCancel={handleCancelCandidates}
            onContinue={() =>
              setFlowState(AddNewAccountFlowState.CONFIRMING_ACCOUNTS)
            }
          />
        );

      case AddNewAccountFlowState.UPDATE_CHOOSING_SYNC_START:
        return (
          <SyncStartModalContent
            candidateAccounts={updateCandidateAccounts}
            expectedCount={expectedNewAccounts}
            onCancel={handleCancelUpdateCandidates}
            onContinue={() =>
              setFlowState(AddNewAccountFlowState.UPDATE_CONFIRMING_ACCOUNTS)
            }
          />
        );

      case AddNewAccountFlowState.SELECT_BANK:
        return (
          <RyeModalContent
            title="Add an account"
            showCloseX={true}
            onClose={closeModal}
            content={
              <InstitutionSearch
                searchQuery={searchQuery}
                onSearchChange={handleSearchChange}
                shouldFocus={shouldFocus}
                onFocusAck={() => setShouldFocus(false)}
                isLoading={isLoading}
                searchResults={searchResults}
                initialInstitutions={initialInstitutions}
                onSelectBank={handleSelectBank}
              />
            }
            actionButtons={[]}
          />
        );

      case AddNewAccountFlowState.PRE_LINK_NOTE:
        return (
          <PreLinkNoteModalContent
            title={`Securely connect to ${selectedInstitution?.name} using Plaid`}
            description={
              <>
                Bread uses Plaid to connect to {selectedInstitution?.name}, and
                we want you to know the following:
              </>
            }
            institutionName={selectedInstitution?.name ?? ""}
            onBack={() => {
              setSelectedInstitution(null);
              setFlowState(AddNewAccountFlowState.SELECT_BANK);
            }}
            onProceed={handleProceedToLink}
            onClose={closeModal}
          />
        );

      case AddNewAccountFlowState.PLAID_LINK_OPENING:
      case AddNewAccountFlowState.LINKING_ACCOUNTS:
        return (
          <PlaidLinkLoaderModalContent
            title={
              flowState === AddNewAccountFlowState.PLAID_LINK_OPENING
                ? "Opening Plaid"
                : "Getting new accounts from Plaid"
            }
          />
        );

      case AddNewAccountFlowState.CONFIRMING_ACCOUNTS: {
        const candidateAccounts = newCandidateAccounts;

        let numSelectedNewAccounts = 0;
        accounts.forEach((account) => {
          if (
            account.institution_id === selectedInstitution?.institution_id &&
            account.status === AccountStatuses.NEW_SELECTED
          ) {
            numSelectedNewAccounts++;
          }
        });

        return (
          <ConfirmAccountsModalContent
            accountsList={candidateAccounts}
            accountUpdatePromises={accountUpdatePromises}
            onCancel={handleCancelCandidates}
            onConfirm={handleConfirmCandidates}
            confirmText={`Add ${numSelectedNewAccounts} Account${
              numSelectedNewAccounts === 1 ? "" : "s"
            }`}
            confirmDisabled={numSelectedNewAccounts === 0}
          />
        );
      }

      case AddNewAccountFlowState.SYNCING:
      case AddNewAccountFlowState.UPDATE_SYNCING:
        return <FinalizingSyncModalContent />;

      case AddNewAccountFlowState.UPDATE_PRE_LINK_NOTE:
        return (
          <UpdatePreLinkNoteModalContent
            title={`Add additional accounts at ${selectedInstitution?.name}`}
            institutionName={selectedInstitution?.name ?? ""}
            plaidItemId={updatePlaidItemId ?? ""}
            onBack={() => {
              setUpdatePlaidItemId(null);
              setSelectedInstitution(null);
              setFlowState(AddNewAccountFlowState.SELECT_BANK);
            }}
            onProceed={handleProceedToUpdateLink}
            onClose={closeModal}
          />
        );

      case AddNewAccountFlowState.UPDATE_PLAID_LINK_OPENING:
      case AddNewAccountFlowState.UPDATE_INITIALIZING_ACCOUNTS:
        return (
          <PlaidLinkLoaderModalContent
            title={
              flowState === AddNewAccountFlowState.UPDATE_PLAID_LINK_OPENING
                ? "Opening Plaid"
                : "Getting new accounts from Plaid"
            }
          />
        );

      case AddNewAccountFlowState.UPDATE_CONFIRMING_ACCOUNTS: {
        const isLoadingUpdateCandidates =
          expectedNewAccounts !== null &&
          updateCandidateAccounts.length < expectedNewAccounts;

        if (isLoadingUpdateCandidates) {
          return (
            <RyeModalContent
              title="Select Accounts to Sync"
              showCloseX={false}
              content={
                <div
                  className={buildClasses(
                    "flex",
                    "flex-col",
                    "items-center",
                    "justify-center",
                    "py-10",
                    "text-on-surface-400",
                    "text-sm",
                  )}
                >
                  <l-line-wobble size="120" speed="2" color="#7d57ff" />
                  <div className="h-6" />
                  <div>Loading candidate accounts...</div>
                </div>
              }
              actionButtons={[]}
            />
          );
        }

        let numSelectedUpdateAccounts = 0;
        accounts.forEach((account) => {
          if (
            account.plaid_item_id === updatePlaidItemId &&
            account.status === AccountStatuses.NEW_SELECTED
          ) {
            numSelectedUpdateAccounts++;
          }
        });

        return (
          <ConfirmAccountsModalContent
            accountsList={updateCandidateAccounts}
            accountUpdatePromises={accountUpdatePromises}
            onCancel={handleCancelUpdateCandidates}
            onConfirm={handleConfirmUpdateCandidates}
            confirmText={`Add ${numSelectedUpdateAccounts} Account${
              numSelectedUpdateAccounts === 1 ? "" : "s"
            }`}
            confirmDisabled={numSelectedUpdateAccounts === 0}
          />
        );
      }
    }
  };

  return <div className={buildClasses("max-w-full")}>{getModalContent()}</div>;
}

// Reusable Helper Components

/** A short, low-key heads-up about something the user will see in Plaid. */
function PreLinkNote({
  icon,
  children,
}: {
  icon: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div
      className={buildClasses(
        "flex",
        "items-start",
        "gap-3",
        "text-sm",
        "text-on-surface-600",
        "leading-relaxed",
      )}
    >
      <RyeIcon
        name={icon}
        size="sm"
        className={buildClasses("text-on-surface-400", "mt-0.5", "shrink-0")}
      />
      <div>{children}</div>
    </div>
  );
}

interface PreLinkNoteModalContentProps {
  title: string;
  description: React.ReactNode;
  institutionName: string;
  onBack: () => void;
  onProceed: () => void;
  onClose: () => void;
}

export function PreLinkNoteModalContent({
  title,
  description,
  institutionName,
  onBack,
  onProceed,
  onClose,
}: PreLinkNoteModalContentProps): JSX.Element {
  return (
    <RyeModalContent
      title={title}
      showCloseX={true}
      onClose={onClose}
      content={
        <div className={buildClasses("w-[40rem]", "flex", "flex-col", "p-2")}>
          <div
            className={buildClasses(
              "text-sm",
              "text-on-surface-800",
              "leading-relaxed",
            )}
          >
            {description}
          </div>
          {/* In the order the user will meet them in Plaid. */}
          <div className={buildClasses("mt-5", "flex", "flex-col", "gap-3")}>
            <PreLinkNote icon="smartphone">
              Bread doesn't need your phone number, so feel free to skip when
              Plaid asks for it.
            </PreLinkNote>
            <PreLinkNote icon="search">
              Plaid will make you search for {institutionName} again. We're
              sorry for the hassle.
            </PreLinkNote>
          </div>
        </div>
      }
      actionButtons={[
        {
          text: "Back",
          vibe: "subdued",
          variant: "outlined",
          action: onBack,
          close: false,
        },
        {
          text: `Open Plaid`,
          vibe: "primary",
          variant: "filled",
          action: () => {
            navigator.clipboard.writeText(institutionName).catch((err) => {
              console.error("Failed to copy institution name:", err);
            });
            onProceed();
          },
          close: false,
        },
      ]}
    />
  );
}

interface UpdatePreLinkNoteModalContentProps {
  title: string;
  description?: React.ReactNode;
  institutionName: string;
  /** The connection being updated, whose current accounts must stay checked. */
  plaidItemId: string;
  onBack: () => void;
  onProceed: () => void;
  onClose: () => void;
}

export function UpdatePreLinkNoteModalContent({
  title,
  description,
  institutionName,
  plaidItemId,
  onBack,
  onProceed,
  onClose,
}: UpdatePreLinkNoteModalContentProps): JSX.Element {
  const accounts = useContext(AccountsContext);
  const linkedAccounts = Array.from(accounts.values())
    .filter(
      (account) =>
        account.plaid_item_id === plaidItemId &&
        account.status === AccountStatuses.ACTIVE,
    )
    .sort((a, b) => a.order_position - b.order_position);

  return (
    <RyeModalContent
      title={title}
      showCloseX={true}
      onClose={onClose}
      content={
        <div
          className={buildClasses(
            "w-[40rem]",
            "flex",
            "flex-col",
            "gap-5",
            "p-2",
          )}
        >
          {description !== undefined && (
            <div
              className={buildClasses(
                "text-sm",
                "text-on-surface-800",
                "leading-relaxed",
              )}
            >
              {description}
            </div>
          )}
          {/* The one note here that can cost the user something, so it stands
              out; listing their accounts gives them something concrete to
              check against in Plaid. */}
          <div
            className={buildClasses(
              "p-4",
              "bg-yellow-50",
              "border",
              "border-yellow-200",
              "rounded-xl",
              "flex",
              "gap-3",
            )}
          >
            <RyeIcon
              name="warning"
              size="md"
              className={buildClasses("text-yellow-700", "shrink-0")}
            />
            <div
              className={buildClasses(
                "flex",
                "flex-col",
                "gap-2",
                "text-sm",
                "leading-relaxed",
              )}
            >
              <div
                className={buildClasses("font-medium", "text-on-surface-900")}
              >
                Important: Keep your existing accounts linked
              </div>
              <div className={buildClasses("text-on-surface-700")}>
                Plaid may let you add <em>or remove</em> accounts, and any
                existing accounts that are left unselected will stop syncing to
                Bread.
              </div>
              {linkedAccounts.length > 0 && (
                <div className={buildClasses("text-on-surface-700")}>
                  You already have these {institutionName} accounts linked:
                </div>
              )}
              {linkedAccounts.length > 0 && (
                <ul className={buildClasses("flex", "flex-col", "gap-1")}>
                  {linkedAccounts.map((account) => (
                    <li
                      key={account.id}
                      className={buildClasses(
                        "flex",
                        "items-center",
                        "gap-2",
                        "text-on-surface-800",
                      )}
                    >
                      <RyeIcon
                        name="check_box"
                        size="sm"
                        fill={true}
                        className={buildClasses("text-purple", "shrink-0")}
                      />
                      <span>{account.name}</span>
                      {account.mask && (
                        <span className={buildClasses("text-on-surface-500")}>
                          ••{account.mask}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      }
      actionButtons={[
        {
          text: "Back",
          vibe: "subdued",
          variant: "outlined",
          action: onBack,
          close: false,
        },
        {
          text: "Open Plaid",
          vibe: "primary",
          variant: "filled",
          action: onProceed,
          close: false,
        },
      ]}
    />
  );
}

interface PlaidLinkLoaderProps {
  title: string;
}

export function PlaidLinkLoaderModalContent({
  title,
}: PlaidLinkLoaderProps): JSX.Element {
  return (
    <RyeModalContent
      title={title}
      showCloseX={false}
      content={
        <div
          className={buildClasses(
            "flex",
            "flex-col",
            "items-center",
            "justify-center",
            "py-10",
            "text-on-surface-400",
            "text-sm",
          )}
        >
          <l-line-wobble size="130" speed="2" color="#7d57ff" />
        </div>
      }
      actionButtons={[]}
    />
  );
}

export function FinalizingSyncModalContent(): JSX.Element {
  return (
    <RyeModalContent
      title="Finalizing new accounts"
      showCloseX={false}
      content={
        <div
          className={buildClasses(
            "w-[40rem]",
            "flex",
            "flex-col",
            "items-center",
            "justify-center",
            "py-10",
            "text-on-surface-400",
            "text-sm",
          )}
        >
          <l-line-wobble size="120" speed="2" color="#7d57ff" />
          <div className="h-6" />
          <div>Finishing up</div>
        </div>
      }
      actionButtons={[]}
    />
  );
}

interface ConfirmAccountsProps {
  accountsList: BreadAccount[];
  accountUpdatePromises: React.MutableRefObject<Promise<void | WriteBatch>[]>;
  onCancel: () => void;
  onConfirm: () => void;
  confirmText: string;
  confirmDisabled?: boolean;
}

export function ConfirmAccountsModalContent({
  accountsList,
  accountUpdatePromises,
  onCancel,
  onConfirm,
  confirmText,
  confirmDisabled = false,
}: ConfirmAccountsProps): JSX.Element {
  return (
    <RyeModalContent
      title="Finalize new accounts to sync"
      showCloseX={false}
      content={
        <div className={buildClasses("w-[40rem]", "flex", "flex-col")}>
          <div
            className={buildClasses("text-sm", "text-on-surface-600", "mb-2")}
          >
            Give us one more confirmation that these are the new accounts you
            want to sync.
          </div>
          <div
            className={buildClasses(
              "max-h-[320px]",
              "overflow-y-auto",
              "border",
              "border-on-surface-200",
              "rounded-lg",
              "mt-3",
            )}
          >
            {accountsList.map((account) => (
              <CandidateAccountRow
                key={account.id}
                account={account}
                accountUpdatePromises={accountUpdatePromises}
              />
            ))}
          </div>
        </div>
      }
      actionButtons={[
        {
          text: "Cancel",
          vibe: "subdued",
          variant: "outlined",
          action: onCancel,
          close: false,
        },
        {
          text: confirmText,
          vibe: "primary",
          variant: "filled",
          action: onConfirm,
          close: false,
          disabled: confirmDisabled,
        },
      ]}
    />
  );
}
